import { ApiException } from "~/interfaces/entities/ApiException"
import { DocumentAnalysis } from "~/interfaces/entities/DocumentAnalysis"
import { DocumentConversion } from "~/interfaces/entities/DocumentConversion"
import { DocumentThumbnail } from "~/interfaces/entities/DocumentThumbnail"
import { logger } from "~/services/Logger"
import { convertToEntitiesArray, convertToEntity } from "~/services/Utils"

const parseError = (error: any): ApiException => {
  if (error?.errors) {
    return {
      code: error.errors[0].code,
      message: error.error?.message ?? error.errors[0].title,
      errors: error.errors,
    }
  }

  if (error.error) {
    return {
      code: error.error.code,
      message: error.error.message,
    }
  }

  if (error.message) {
    return {
      code: "Unspecified",
      message: error.message,
    }
  }

  return {
    code: "Unspecified",
    message: JSON.stringify(error),
  }
}

export class DocumentApi {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async post<T = any>(url: string, params: any) {
    try {
      const headers = {
        "Content-Type": "application/json",
      }

      const response = await fetch(`${this.baseUrl}${url}`, {
        headers,
        method: "POST",
        body: JSON.stringify(params),
      })

      if ([200, 201, 204].indexOf(response.status) === -1) {
        const error = await response.json()
        const parsedError = parseError(error)

        if ([400, 401].indexOf(response.status) === -1) {
          logger.error(
            Error(`code: ${parsedError.code} message: ${parsedError.message}`)
          )
        }

        throw parsedError
      }
      const data = await response.json()

      return data as T
    } catch (error) {
      throw error
    }
  }

  public async processDocumentAnalysis(payload: {
    url: string
  }): Promise<DocumentAnalysis> {
    const response: any = await this.post("/", {
      data: {
        type: "DocumentAnalysisRequest",
        attributes: {
          url: payload.url,
        },
      },
    })
    return convertToEntity<DocumentAnalysis>(response.data)
  }

  public async processDocumentThumbnail(payload: {
    url: string
    pageIndex: number
  }): Promise<DocumentThumbnail[]> {
    const response: any = await this.post("/", {
      data: {
        type: "DocumentThumbnailRequest",
        attributes: {
          url: payload.url,
          width: 250,
          pageLimit: 1,
          pageRange: payload.pageIndex,
        },
      },
    })
    return convertToEntitiesArray<DocumentThumbnail>(response)
  }

  public async processDocumentConversion(payload: {
    url: string
  }): Promise<DocumentConversion> {
    const response: any = await this.post("/", {
      data: {
        type: "DocumentConversionRequest",
        attributes: {
          url: payload.url,
        },
      },
    })

    return convertToEntity<DocumentConversion>(response.data)
  }
}
